/**
 * Which showRelatedMedia lookups are still running. The voice service marks
 * each one; chat surfaces read it through useMediaLookupPending to show a
 * placeholder card until the links or pictures arrive.
 */
// Each running lookup, and whether it has already put something on screen.
const running = new Map<symbol, boolean>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

export const mediaLookups = {
  /** Marks a lookup as running; call `settled` once it is over, and `shown` when its first results are on screen. */
  begin(): { shown: () => void; settled: () => void } {
    const lookup = Symbol("media-lookup");
    running.set(lookup, false);
    notify();
    return {
      shown: () => {
        if (running.get(lookup) !== false) return;
        running.set(lookup, true);
        notify();
      },
      settled: () => {
        if (running.delete(lookup)) notify();
      },
    };
  },

  /** Drops every running lookup, for when the conversation they belong to is over. */
  clear(): void {
    if (running.size === 0) return;
    running.clear();
    notify();
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  isPending: (): boolean => running.size > 0,

  /** True when every lookup still running has shown its first results and is only looking for more. */
  isAddingMore: (): boolean => running.size > 0 && [...running.values()].every(Boolean),
};
